/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import * as Yup from 'yup';

import { Form as UnformForm } from '@unform/web';
import { FormHandles } from '@unform/core';

import { FiEdit, FiSearch, FiTrash2, FiPlus, FiPercent, FiList } from 'react-icons/fi';

import Toolbar from '../../components/Toolbar';
import LoadingAnimation from '../../components/Loading';

import api from '../../services/api';
import Input from '../../components/Input';
import Button from '../../components/Button';
import getValidationErrors from '../../utils/getValidationErros';
import { useToast } from '../../hooks/toast';

import {
  Container,
  Form,
  Content,
  HeaderRow,
  Title,
  Tabs,
  TabButton,
  SectionHeader,
  SectionTitle,
  PrimaryButton,
  Table,
  Actions,
  IconButton,
  ModalTitle,
  ModalFooter,
  TesouroField,
} from './styles';

interface AssetDistribution {
  type: 'STOCK' | 'FII' | 'TD';
  name: string;
  code: string;
  weight: number;
}

type AssetDistributionGroups = {
  STOCK: AssetDistribution[];
  FII: AssetDistribution[];
  TD: AssetDistribution[];
};

const PortfolioV2: React.FC = () => {
  const { addToast } = useToast();

  const [stocks, setStocks] = useState<AssetDistribution[]>([]);
  const [fiis, setFiis] = useState<AssetDistribution[]>([]);
  const [tesouro, setTesouro] = useState<AssetDistribution[]>([]);

  const [totalStocksWeight, setTotalStocksWeight] = useState<number>(0);
  const [totalFiisWeight, setTotalFiisWeight] = useState<number>(0);
  const [totalTesouroWeight, setTotalTesouroWeight] = useState<number>(0);

  const [activeTab, setActiveTab] = useState<'STOCK' | 'FII' | 'TD'>('STOCK');

  // Modals
  const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false);
  const [isAddFiiModalOpen, setIsAddFiiModalOpen] = useState(false);
  const [isAddTesouroModalOpen, setIsAddTesouroModalOpen] = useState(false);

  const [isViewStockModalOpen, setIsViewStockModalOpen] = useState(false);
  const [isViewFiiModalOpen, setIsViewFiiModalOpen] = useState(false);
  const [isViewTesouroModalOpen, setIsViewTesouroModalOpen] = useState(false);

  const [viewAssetIndex, setViewAssetIndex] = useState(0);

  const [isDeleteStockModalOpen, setIsDeleteStockModalOpen] = useState(false);
  const [isDeleteFiiModalOpen, setIsDeleteFiiModalOpen] = useState(false);
  const [isDeleteTesouroModalOpen, setIsDeleteTesouroModalOpen] = useState(false);

  const [isEditStockModalOpen, setIsEditStockModalOpen] = useState(false);
  const [isEditFiiModalOpen, setIsEditFiiModalOpen] = useState(false);
  const [isEditTesouroModalOpen, setIsEditTesouroModalOpen] = useState(false);

  const [tesouroInputs, setTesouroInputs] = useState(['']);

  const [loading, setLoading] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);
  const addStockFormRef = useRef<FormHandles>(null);
  const addFiiFormRef = useRef<FormHandles>(null);
  const addTesouroFormRef = useRef<FormHandles>(null);

  useEffect(() => {
    api.get<AssetDistribution[]>('wallet/distribution').then((response) => {
      const { STOCK, FII, TD } = response.data.reduce<AssetDistributionGroups>(
        (acc, item) => {
          acc[item.type].push(item);
          return acc;
        },
        { STOCK: [], FII: [], TD: [] },
      );

      const stocksWeights = STOCK.reduce((acc, stock) => acc + stock.weight, 0);
      const fiisWeights = FII.reduce((acc, fii) => acc + fii.weight, 0);
      const tesouroWeights = TD.reduce((acc, t) => acc + t.weight, 0);

      setStocks(STOCK);
      setTotalStocksWeight(stocksWeights);
      setFiis(FII);
      setTotalFiisWeight(fiisWeights);
      setTesouro(TD);
      setTotalTesouroWeight(tesouroWeights);
      setLoading(false);
    });
  }, []);

  const handleToggleModal = useCallback(
    (modalType: string, index?: number) => {
      switch (modalType) {
        case 'addStock': setIsAddStockModalOpen((v) => !v); break;
        case 'viewStock': if (typeof index === 'number') setViewAssetIndex(index); setIsViewStockModalOpen((v) => !v); break;
        case 'deleteStock': if (typeof index === 'number') setViewAssetIndex(index); setIsDeleteStockModalOpen((v) => !v); break;
        case 'editStock': if (typeof index === 'number') setViewAssetIndex(index); setIsEditStockModalOpen((v) => !v); break;
        case 'addFii': setIsAddFiiModalOpen((v) => !v); break;
        case 'viewFii': if (typeof index === 'number') setViewAssetIndex(index); setIsViewFiiModalOpen((v) => !v); break;
        case 'deleteFii': if (typeof index === 'number') setViewAssetIndex(index); setIsDeleteFiiModalOpen((v) => !v); break;
        case 'editFii': if (typeof index === 'number') setViewAssetIndex(index); setIsEditFiiModalOpen((v) => !v); break;
        case 'addTesouro': setIsAddTesouroModalOpen((v) => !v); break;
        case 'viewTesouro': if (typeof index === 'number') setViewAssetIndex(index); setIsViewTesouroModalOpen((v) => !v); break;
        case 'deleteTesouro': if (typeof index === 'number') setViewAssetIndex(index); setIsDeleteTesouroModalOpen((v) => !v); break;
        case 'editTesouro': {
          const idx = typeof index === 'number' ? index : viewAssetIndex;
          if (!tesouro[idx]) break;
          const newTesouroInputs = tesouro[idx].name.split(',').map((s) => s.trim());
          setViewAssetIndex(idx);
          setTesouroInputs([...newTesouroInputs]);
          setIsEditTesouroModalOpen((v) => !v);
          break;
        }
        default: break;
      }
    },
    [tesouro, viewAssetIndex],
  );

  const handleAddAsset = useCallback(
    async (assetData: AssetDistribution) => {
      addStockFormRef.current?.setErrors({});
      addFiiFormRef.current?.setErrors({});
      addTesouroFormRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          code: Yup.string().required('Código é obrigatório'),
          name: Yup.string().required('Nome é obrigatório'),
          weight: Yup.number().typeError('Valor deve ser um número').required('Informe o peso do ativo na carteira'),
        });
        await schema.validate(assetData, { abortEarly: false });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          addStockFormRef.current?.setErrors(errors);
          addFiiFormRef.current?.setErrors(errors);
          addTesouroFormRef.current?.setErrors(errors);
          return;
        }
      }

      const asset = {
        type: assetData.type,
        name: assetData.name,
        code: assetData.code,
        weight: assetData.weight * 100,
      } as AssetDistribution;

      switch (assetData.type) {
        case 'STOCK': {
          const hasStock = stocks.find((s) => s.code === assetData.code);
          if (hasStock) {
            addStockFormRef.current?.setErrors({ code: 'stock already in wallet' });
            return;
          }
          setStocks([...stocks, asset]);
          setTotalStocksWeight(totalStocksWeight + asset.weight);
          setIsAddStockModalOpen(false);
          break;
        }
        case 'FII': {
          const hasFii = fiis.find((f) => f.code === assetData.code);
          if (hasFii) {
            addFiiFormRef.current?.setErrors({ code: 'fii already in wallet' });
            return;
          }
          setFiis([...fiis, asset]);
          setTotalFiisWeight(totalFiisWeight + asset.weight);
          setIsAddFiiModalOpen(false);
          break;
        }
        case 'TD': {
          const hasTesouro = tesouro.find((t) => t.code === assetData.code);
          if (hasTesouro) {
            addTesouroFormRef.current?.setErrors({ code: 'wallet already has group with this name' });
            return;
          }
          const existingAssets = tesouro.flatMap((item) => item.name.split(',').map((v) => v.trim()));
          const hasAsset = tesouroInputs.some((v) => existingAssets.includes(v));
          if (hasAsset) {
            addTesouroFormRef.current?.setErrors({ name: 'wallet already has one or more of these assets is another group' });
            return;
          }
          const assetName = tesouroInputs.join(',');
          asset.name = assetName;
          setTesouroInputs(['']);
          setTesouro([...tesouro, asset]);
          setTotalTesouroWeight(totalTesouroWeight + asset.weight);
          setIsAddTesouroModalOpen(false);
          break;
        }
        default: break;
      }
    },
    [stocks, fiis, tesouro, tesouroInputs, totalStocksWeight, totalFiisWeight, totalTesouroWeight],
  );

  const handleSaveAsset = useCallback(
    async (assetData: AssetDistribution) => {
      addStockFormRef.current?.setErrors({});
      addFiiFormRef.current?.setErrors({});
      addTesouroFormRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          code: Yup.string().required('Código é obrigatório'),
          name: Yup.string().required('Nome é obrigatório'),
          weight: Yup.number().typeError('Valor deve ser um número').required('Informe o peso do ativo na carteira'),
        });
        await schema.validate(assetData, { abortEarly: false });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          addStockFormRef.current?.setErrors(errors);
          addFiiFormRef.current?.setErrors(errors);
          addTesouroFormRef.current?.setErrors(errors);
          return;
        }
      }

      switch (assetData.type) {
        case 'STOCK': {
          let newTotal = totalStocksWeight;
          const list = [...stocks];
          newTotal = newTotal - list[viewAssetIndex].weight + assetData.weight * 100;
          list[viewAssetIndex].name = assetData.name;
          list[viewAssetIndex].code = assetData.code;
          list[viewAssetIndex].weight = assetData.weight * 100;
          setStocks(list);
          setTotalStocksWeight(newTotal);
          setIsEditStockModalOpen(false);
          break;
        }
        case 'FII': {
          let newTotal = totalFiisWeight;
          const list = [...fiis];
          newTotal = newTotal - list[viewAssetIndex].weight + assetData.weight * 100;
          list[viewAssetIndex].name = assetData.name;
          list[viewAssetIndex].code = assetData.code;
          list[viewAssetIndex].weight = assetData.weight * 100;
          setFiis(list);
          setTotalFiisWeight(newTotal);
          setIsEditFiiModalOpen(false);
          break;
        }
        case 'TD': {
          let newTotal = totalTesouroWeight;
          const list = [...tesouro];
          newTotal = newTotal - list[viewAssetIndex].weight + assetData.weight * 100;
          list[viewAssetIndex].code = assetData.code;
          list[viewAssetIndex].weight = assetData.weight * 100;
          list[viewAssetIndex].name = tesouroInputs.join(',');
          setTesouroInputs(['']);
          setTesouro(list);
          setTotalTesouroWeight(newTotal);
          setIsEditTesouroModalOpen(false);
          break;
        }
        default: break;
      }
    },
    [stocks, fiis, tesouro, tesouroInputs, viewAssetIndex, totalStocksWeight, totalFiisWeight, totalTesouroWeight],
  );

  const handleAddTesouroAsset = useCallback(() => {
    setTesouroInputs((curr) => [...curr, '']);
  }, []);
  const handleRemoveTesouroAsset = useCallback((index) => {
    setTesouroInputs((curr) => {
      const n = [...curr];
      n.splice(index, 1);
      return n;
    });
  }, []);
  const handleInputChange = useCallback((index, event) => {
    const value = event && event.target ? event.target.value : '';
    setTesouroInputs((curr) => {
      const n = [...curr];
      n[index] = value;
      return n;
    });
  }, []);

  const removeAssetByIndex = useCallback(
    (assetType, index) => {
      switch (assetType) {
        case 'stock': {
          const list = [...stocks];
          const removed = list.splice(index, 1);
          setStocks(list);
          setTotalStocksWeight(totalStocksWeight - removed[0].weight);
          break;
        }
        case 'fii': {
          const list = [...fiis];
          const removed = list.splice(index, 1);
          setFiis(list);
          setTotalFiisWeight(totalFiisWeight - removed[0].weight);
          break;
        }
        case 'tesouro': {
          const list = [...tesouro];
          const removed = list.splice(index, 1);
          setTesouro(list);
          setTotalTesouroWeight(totalTesouroWeight - removed[0].weight);
          break;
        }
        default: break;
      }
    },
    [stocks, fiis, tesouro, totalStocksWeight, totalFiisWeight, totalTesouroWeight],
  );

  const handleSubmitWallet = useCallback(async () => {
    const payload = { stocks, fiis, tesouro };
    try {
      await api.post('wallet/setup', payload);
      addToast({ type: 'success', title: 'Registro criado', description: 'Conta cadastrada com sucesso!' });
    } catch (err: any) {
      const errorMessage = err.response.data.message;
      addToast({ type: 'error', title: 'Erro no cadastro de carteira', description: errorMessage });
    }
  }, [stocks, fiis, tesouro, addToast]);

  if (loading) {
    return (
      <Container>
        <Toolbar />
        <Form onSubmit={() => {}}>{/* spacer */}</Form>
        <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingAnimation />
        </div>
      </Container>
    );
  }

  const currentList = activeTab === 'STOCK' ? stocks : activeTab === 'FII' ? fiis : tesouro;
  const currentTotal = activeTab === 'STOCK' ? totalStocksWeight : activeTab === 'FII' ? totalFiisWeight : totalTesouroWeight;
  const sectionTitle =
    activeTab === 'STOCK'
      ? `Ações - ${(currentTotal / 100).toFixed(2)}%`
      : activeTab === 'FII'
      ? `FIIs - ${(currentTotal / 100).toFixed(2)}%`
      : `Tesouro Direto - ${(currentTotal / 100).toFixed(2)}%`;

  return (
    <Container ref={containerRef}>
      <Toolbar />
      <Form onSubmit={() => {}}>{/* spacer */}</Form>

      {/* Modals - Add */}
      <Modal open={isAddStockModalOpen} onClose={() => handleToggleModal('addStock')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Adicionar ação</ModalTitle>
        <UnformForm ref={addStockFormRef} onSubmit={handleAddAsset}>
          <Input name="type" hidden value="STOCK" />
          <Input name="code" icon={FiList} placeholder="Código do ativo" />
          <Input name="name" icon={FiList} placeholder="Nome do ativo" />
          <Input name="weight" icon={FiPercent} placeholder="Peso do ativo" />
          <ModalFooter>
            <Button type="button" onClick={() => handleToggleModal('addStock')}>Cancelar</Button>
            <Button type="submit">Adicionar</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      <Modal open={isAddFiiModalOpen} onClose={() => handleToggleModal('addFii')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Adicionar FII</ModalTitle>
        <UnformForm ref={addFiiFormRef} onSubmit={handleAddAsset}>
          <Input name="type" hidden value="FII" />
          <Input name="code" icon={FiList} placeholder="Código do ativo" />
          <Input name="name" icon={FiList} placeholder="Nome do ativo" />
          <Input name="weight" icon={FiPercent} placeholder="Peso do ativo" />
          <ModalFooter>
            <Button type="button" onClick={() => handleToggleModal('addFii')}>Cancelar</Button>
            <Button type="submit">Adicionar</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      <Modal open={isAddTesouroModalOpen} onClose={() => handleToggleModal('addTesouro')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Adicionar Grupo de Tesouro Direto</ModalTitle>
        <UnformForm ref={addTesouroFormRef} onSubmit={handleAddAsset}>
          <Input name="type" hidden value="TD" />
          <Input name="code" icon={FiList} placeholder="Nome do grupo" />
          {tesouroInputs.map((input, index) => (
            <TesouroField key={`new-td-${index}`}>
              <Input name="name" icon={FiList} placeholder="Nome do título" onChange={(e) => handleInputChange(index, e)} />
              <IconButton tone="danger" onClick={() => handleRemoveTesouroAsset(index)}>
                <FiTrash2 size={16} />
              </IconButton>
            </TesouroField>
          ))}
          <PrimaryButton variant="primary" type="button" onClick={handleAddTesouroAsset}><FiPlus size={16} /> Adicionar título</PrimaryButton>
          <Input name="weight" icon={FiPercent} placeholder="Peso do grupo" />
          <ModalFooter>
            <Button type="button" onClick={() => handleToggleModal('addTesouro')}>Cancelar</Button>
            <Button type="submit">Adicionar</Button>
          </ModalFooter>
        </UnformForm>
      </Modal>

      {/* Modals - View/Delete/Edit for each type (reuse original logic) */}
      {/* View Stock */}
      <Modal open={isViewStockModalOpen} onClose={() => handleToggleModal('viewStock')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Detalhes do ativo</ModalTitle>
        {stocks[viewAssetIndex] && (
          <div>
            <div style={{ margin: '8px 0' }}><strong>Código:</strong> {stocks[viewAssetIndex].code}</div>
            <div style={{ margin: '8px 0' }}><strong>Nome:</strong> {stocks[viewAssetIndex].name}</div>
            <div style={{ margin: '8px 0' }}><strong>Peso:</strong> {(stocks[viewAssetIndex].weight / 100).toFixed(2)}</div>
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('viewStock')}>Fechar</Button>
            </ModalFooter>
          </div>
        )}
      </Modal>

      {/* Delete Stock */}
      <Modal open={isDeleteStockModalOpen} onClose={() => handleToggleModal('deleteStock')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Detalhes do ativo</ModalTitle>
        {stocks[viewAssetIndex] && (
          <div>
            <div style={{ margin: '8px 0' }}><strong>Código:</strong> {stocks[viewAssetIndex].code}</div>
            <div style={{ margin: '8px 0' }}><strong>Nome:</strong> {stocks[viewAssetIndex].name}</div>
            <div style={{ margin: '8px 0' }}><strong>Peso:</strong> {(stocks[viewAssetIndex].weight / 100).toFixed(2)}</div>
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('deleteStock')}>Fechar</Button>
              <Button type="button" onClick={() => { handleToggleModal('deleteStock'); removeAssetByIndex('stock', viewAssetIndex); }}>Deletar</Button>
            </ModalFooter>
          </div>
        )}
      </Modal>

      {/* Edit Stock */}
      <Modal open={isEditStockModalOpen} onClose={() => handleToggleModal('editStock')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Editar ação</ModalTitle>
        {stocks[viewAssetIndex] && (
          <UnformForm ref={addStockFormRef} onSubmit={handleSaveAsset}>
            <Input name="type" hidden value="STOCK" />
            <Input name="code" icon={FiList} placeholder="Código do ativo" defaultValue={stocks[viewAssetIndex].code} />
            <Input name="name" icon={FiList} placeholder="Nome do ativo" defaultValue={stocks[viewAssetIndex].name} />
            <Input name="weight" icon={FiPercent} placeholder="Peso do ativo" defaultValue={(stocks[viewAssetIndex].weight / 100).toFixed(2)} />
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('editStock')}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </ModalFooter>
          </UnformForm>
        )}
      </Modal>

      {/* View/Delete/Edit FII */}
      <Modal open={isViewFiiModalOpen} onClose={() => handleToggleModal('viewFii')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Detalhes do ativo</ModalTitle>
        {fiis[viewAssetIndex] && (
          <div>
            <div style={{ margin: '8px 0' }}><strong>Código:</strong> {fiis[viewAssetIndex].code}</div>
            <div style={{ margin: '8px 0' }}><strong>Nome:</strong> {fiis[viewAssetIndex].name}</div>
            <div style={{ margin: '8px 0' }}><strong>Peso:</strong> {(fiis[viewAssetIndex].weight / 100).toFixed(2)}</div>
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('viewFii')}>Fechar</Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
      <Modal open={isDeleteFiiModalOpen} onClose={() => handleToggleModal('deleteFii')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Detalhes do ativo</ModalTitle>
        {fiis[viewAssetIndex] && (
          <div>
            <div style={{ margin: '8px 0' }}><strong>Código:</strong> {fiis[viewAssetIndex].code}</div>
            <div style={{ margin: '8px 0' }}><strong>Nome:</strong> {fiis[viewAssetIndex].name}</div>
            <div style={{ margin: '8px 0' }}><strong>Peso:</strong> {(fiis[viewAssetIndex].weight / 100).toFixed(2)}</div>
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('deleteFii')}>Fechar</Button>
              <Button type="button" onClick={() => { handleToggleModal('deleteFii'); removeAssetByIndex('fii', viewAssetIndex); }}>Deletar</Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
      <Modal open={isEditFiiModalOpen} onClose={() => handleToggleModal('editFii')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Editar FII</ModalTitle>
        {fiis[viewAssetIndex] && (
          <UnformForm ref={addFiiFormRef} onSubmit={handleSaveAsset}>
            <Input name="type" hidden value="FII" />
            <Input name="code" icon={FiList} placeholder="Código do ativo" defaultValue={fiis[viewAssetIndex].code} />
            <Input name="name" icon={FiList} placeholder="Nome do ativo" defaultValue={fiis[viewAssetIndex].name} />
            <Input name="weight" icon={FiPercent} placeholder="Peso do ativo" defaultValue={(fiis[viewAssetIndex].weight / 100).toFixed(2)} />
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('editFii')}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </ModalFooter>
          </UnformForm>
        )}
      </Modal>

      {/* View/Delete/Edit Tesouro */}
      <Modal open={isViewTesouroModalOpen} onClose={() => handleToggleModal('viewTesouro')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Detalhes do ativo</ModalTitle>
        {tesouro[viewAssetIndex] && (
          <div>
            <div style={{ margin: '8px 0' }}><strong>Nome do grupo:</strong> {tesouro[viewAssetIndex].code}</div>
            <div style={{ margin: '8px 0' }}><strong>Títulos:</strong> {tesouro[viewAssetIndex].name}</div>
            <div style={{ margin: '8px 0' }}><strong>Peso:</strong> {(tesouro[viewAssetIndex].weight / 100).toFixed(2)}</div>
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('viewTesouro')}>Fechar</Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
      <Modal open={isDeleteTesouroModalOpen} onClose={() => handleToggleModal('deleteTesouro')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Detalhes do ativo</ModalTitle>
        {tesouro[viewAssetIndex] && (
          <div>
            <div style={{ margin: '8px 0' }}><strong>Nome do grupo:</strong> {tesouro[viewAssetIndex].code}</div>
            <div style={{ margin: '8px 0' }}><strong>Títulos no grupo:</strong> {tesouro[viewAssetIndex].name}</div>
            <div style={{ margin: '8px 0' }}><strong>Peso:</strong> {(tesouro[viewAssetIndex].weight / 100).toFixed(2)}</div>
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('deleteTesouro')}>Fechar</Button>
              <Button type="button" onClick={() => { handleToggleModal('deleteTesouro'); removeAssetByIndex('tesouro', viewAssetIndex); }}>Deletar</Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
      <Modal open={isEditTesouroModalOpen} onClose={() => handleToggleModal('editTesouro')} showCloseIcon={false} center classNames={{ modal: 'v2Modal' }} container={containerRef.current as Element}>
        <ModalTitle>Editar Tesouro</ModalTitle>
        {tesouro[viewAssetIndex] && (
          <UnformForm ref={addTesouroFormRef} onSubmit={handleSaveAsset}>
            <Input name="type" hidden value="TD" />
            <Input name="code" icon={FiList} placeholder="Nome do grupo" defaultValue={tesouro[viewAssetIndex].code} />
            {tesouroInputs.map((input, index) => (
              <TesouroField key={`edit-td-${index}`}>
                <Input name="name" icon={FiList} placeholder="Nome do título" onChange={(e) => handleInputChange(index, e)} defaultValue={input} />
                <IconButton tone="danger" onClick={() => handleRemoveTesouroAsset(index)}>
                  <FiTrash2 size={16} />
                </IconButton>
              </TesouroField>
            ))}
            <PrimaryButton variant="primary" type="button" onClick={handleAddTesouroAsset}><FiPlus size={16} /> Adicionar título</PrimaryButton>
            <Input name="weight" icon={FiPercent} placeholder="Peso do ativo" defaultValue={(tesouro[viewAssetIndex].weight / 100).toFixed(2)} />
            <ModalFooter>
              <Button type="button" onClick={() => handleToggleModal('editTesouro')}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </ModalFooter>
          </UnformForm>
        )}
      </Modal>

      <Content>
        <HeaderRow>
          <Title>Carteira de investimentos</Title>
        </HeaderRow>

        <SectionHeader>
          <Tabs>
            <TabButton active={activeTab === 'STOCK'} onClick={() => setActiveTab('STOCK')}>Ações</TabButton>
            <TabButton active={activeTab === 'FII'} onClick={() => setActiveTab('FII')}>FIIs</TabButton>
            <TabButton active={activeTab === 'TD'} onClick={() => setActiveTab('TD')}>Tesouro Direto</TabButton>
          </Tabs>
          <PrimaryButton variant="green" onClick={handleSubmitWallet}>Salvar carteira</PrimaryButton>
        </SectionHeader>

        <SectionHeader>
          <SectionTitle>{sectionTitle}</SectionTitle>
          {activeTab === 'STOCK' && (
            <PrimaryButton variant="primary" onClick={() => handleToggleModal('addStock')}><FiPlus size={16} /> Adicionar</PrimaryButton>
          )}
          {activeTab === 'FII' && (
            <PrimaryButton variant="primary" onClick={() => handleToggleModal('addFii')}><FiPlus size={16} /> Adicionar</PrimaryButton>
          )}
          {activeTab === 'TD' && (
            <PrimaryButton variant="primary" onClick={() => handleToggleModal('addTesouro')}><FiPlus size={16} /> Adicionar</PrimaryButton>
          )}
        </SectionHeader>

        <Table>
          <thead>
            <tr>
              <th>Código</th>
              <th>Nome</th>
              <th>Peso</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {currentList.map((item, index) => (
              <tr key={`${item.code}-${index}`}>
                <td data-label="Código">{item.code}</td>
                <td data-label="Nome">{item.name}</td>
                <td data-label="Peso">{(item.weight / 100).toFixed(2)}</td>
                <td data-label="Ações">
                  <Actions>
                    <IconButton onClick={() => { handleToggleModal(activeTab === 'STOCK' ? 'editStock' : activeTab === 'FII' ? 'editFii' : 'editTesouro', index); }}>
                      <FiEdit size={16} />
                    </IconButton>
                    <IconButton onClick={() => { handleToggleModal(activeTab === 'STOCK' ? 'viewStock' : activeTab === 'FII' ? 'viewFii' : 'viewTesouro', index); }}>
                      <FiSearch size={16} />
                    </IconButton>
                    <IconButton tone="danger" onClick={() => { handleToggleModal(activeTab === 'STOCK' ? 'deleteStock' : activeTab === 'FII' ? 'deleteFii' : 'deleteTesouro', index); }}>
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Actions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Footer save removed: moved next to tabs */}
      </Content>
    </Container>
  );
};

export default PortfolioV2;
